import { format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { z } from "zod";

import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";

import ContentEditorField from "components/ContentEditor/ContentEditorField";
import ContentPagePreview from "components/molecules/ContentPagePreview";
import FeedPreview from "components/molecules/FeedPreview";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import { ErrorWrapper } from "components/organisms/form/BaseField";
import Form, { useFormValue } from "components/organisms/form/Form";
import FormButton from "components/organisms/form/FormButton";
import MultiSelectField from "components/organisms/form/MultiSelectField";
import SelectField from "components/organisms/form/SelectField";
import TextField from "components/organisms/form/TextField";
import TextareaField from "components/organisms/form/TextareaField";
import ToggleField from "components/organisms/form/ToggleField";
import { SingleInnholdQuery, useCountUsersWithSegmentsQuery, useSegmentsQuery, useUpsertContentMutation } from "pages/Content/FeedCards/Content.graphql-gen";
import { dateFormat } from "utils/Utils";

import { ContentLinkType } from "generated/graphql-types";

export function featureFlagLabelToGroup(label: string) {
	if (label.startsWith("NO")) {
		return "No";
	} else if (label.startsWith("Klassifisering:")) {
		return "Klassifisering";
	} else if (label.startsWith("Gruppe:")) {
		return "Gruppe";
	} else if (label.startsWith("Laget mitt:")) {
		return "Laget mitt";
	} else if (label.startsWith("Produkt:")) {
		return "Produkt";
	} else if (label.startsWith("Netteier:")) {
		return "Netteier";
	} else if (label.startsWith("NetteierGLN:")) {
		return "Netteier GLN";
	} else {
		return "Misc";
	}
}

export const ContentSchema = z.object({
	title: z.string().min(1),
	content: z.string().min(1),
	published: z.boolean(),
	segmentIn: z.array(z.string()),
	segmentNotIn: z.array(z.string()),
	image: z
		.object({
			url: z.string().optional(),
			reference: z.string().optional(),
			provider: z.string().optional(),
		})
		.optional(),
	link: z
		.object({
			url: z.string().optional(),
			type: z.nativeEnum(ContentLinkType).optional(),
			view: z.string().optional(),
			viewType: z.string().optional(),
		})
		.optional(),
	pageTitle: z.string().optional(),
	pageImage: z
		.object({
			url: z.string().optional(),
			reference: z.string().optional(),
			provider: z.string().optional(),
		})
		.optional(),
	pageContent: z
		.object({
			html: z.string().optional(),
			json: z.string().optional(),
		})
		.optional(),

	publishDate: z.string().min(1),
});

export default function CreateContentPage() {
	const navigate = useNavigate();
	const [upsertContent, { loading, error }] = useUpsertContentMutation();
	const [linkType, setLinkType] = useState<ContentLinkType>(ContentLinkType.WEB);
	const [previewState, setPreviewState] = useState<Partial<SingleInnholdQuery["singleContent"]>>({});

	return (
		<div>
			<MainLayoutPageHeader title="Nytt feedkort" />
			<Flex gap="16px">
				<Form
					loading={loading}
					value={{
						link: {
							type: linkType,
						},
						pageContent: {
							json: "",
							html: "",
						},
					}}
					onSubmit={async (data) => {
						const id = uuid();

						const { error } = await upsertContent({
							variables: {
								content: {
									id,
									...data,
									publishDate: new Date(data.publishDate).toISOString(),
								},
							},
						});

						!error && navigate(`/content/${id}`);
					}}
					schema={ContentSchema}
				>
					<Flex direction={Direction.column} gap="16px">
						<TextField
							name="title"
							label="Tittel"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								const target = e.target;
								setPreviewState((oldState) => {
									return {
										...oldState,
										title: target.value || "",
									};
								});
							}}
						/>
						<TextareaField
							name="content"
							label="Innhold"
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
								const target = e.target;
								setPreviewState((oldState) => {
									return {
										...oldState,
										content: target.value || "",
									};
								});
							}}
						/>

						<TextField type="datetime-local" name="publishDate" label="Publiseringsdato" defaultValue={format(new Date(), dateFormat)} />

						<div>
							<TextField
								name="image.url"
								label="Bilde"
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									const target = e.target;
									setPreviewState((oldState) => {
										return {
											...oldState,
											image: {
												...oldState?.image,
												url: target.value || "",
											},
										};
									});
								}}
							/>
							<TextField name="image.reference" type="hidden" value="" />
							<TextField name="image.provider" type="hidden" value="" />
						</div>
						<div>
							<SelectField
								name="link.type"
								label="Gå til"
								options={[
									{ value: "NO_LINK", label: "- vis kun innhold -" },
									{ value: "WEB", label: "Lenke" },
									{ value: "APP_CONTENT_PAGE", label: "Innholdsside i app" },
								]}
								onChange={(linkType) => setLinkType(linkType as ContentLinkType)}
							/>
							{linkType === "NO_LINK" && (
								<div style={{ display: "none" }}>
									<TextField name="link.url" type="hidden" />
									<TextField name="link.view" type="hidden" value="" />
									<TextField name="link.viewType" type="hidden" value="" />
								</div>
							)}
							{linkType === "APP" && (
								<div>
									<TextField name="link.url" label="Lenke" />
									<TextField name="link.view" label="View" />
									<SelectField
										label="Navigeringstype"
										defaultValue={"stack"}
										name="link.viewType"
										options={[
											{ value: "stack", label: "Stack navigering" },
											{ value: "modal", label: "Modal" },
											{ value: "router", label: "Router (bunnmeny)" },
										]}
									/>
								</div>
							)}
							{linkType === "WEB" && (
								<div>
									<TextField name="link.url" label="Lenke" />
									<TextField name="link.view" type="hidden" value="" />
									<TextField name="link.viewType" type="hidden" value="" />
								</div>
							)}
							{linkType === "APP_CONTENT_PAGE" && (
								<div style={{ display: "none" }}>
									<TextField name="link.url" value="" type="hidden" />
									<TextField name="link.view" type="hidden" value="content_page" />
									<TextField name="link.viewType" type="hidden" value="stack" />
								</div>
							)}
						</div>
						{linkType === "APP_CONTENT_PAGE" && (
							<Flex direction={Direction.column} gap="16px">
								<TextField
									name="pageTitle"
									label="Sidetittel"
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const target = e.target;
										setPreviewState((oldState) => {
											return {
												...oldState,
												pageTitle: target.value || "",
											};
										});
									}}
								/>
								<div>
									<TextField
										name="pageImage.url"
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
											const target = e.target;
											setPreviewState((oldState) => {
												return {
													...oldState,
													pageImage: {
														...oldState?.pageImage,
														url: target.value || "",
													},
												};
											});
										}}
										label="Bilde"
									/>
									<TextField name="pageImage.reference" type="hidden" value="" />
									<TextField name="pageImage.provider" type="hidden" value="" />
								</div>
								<ContentEditorField
									name="pageContent"
									label="Sideinnhold"
									onChange={(value) => {
										setPreviewState((oldState) => {
											return {
												...oldState,
												pageContent: value,
											};
										});
									}}
								/>
							</Flex>
						)}

						<ContentSegmentsEditor />

						{error && <ErrorWrapper>{error.message}</ErrorWrapper>}
						<ToggleField name="published" label="Publiser" defaultChecked={false} />
						<FormButton label="Lagre innhold" />
					</Flex>
				</Form>
				<Flex direction={Direction.column} gap="16px" style={{ width: "390px" }}>
					<FeedPreview
						title={previewState?.title || ""}
						content={previewState?.content || ""}
						date={previewState?.publishDate || new Date()}
						imageUrl={previewState?.image?.url || ""}
					/>
					{linkType === "APP_CONTENT_PAGE" && (
						<ContentPagePreview
							title={previewState?.pageTitle || ""}
							contentHtml={previewState?.pageContent?.html || ""}
							imageUrl={previewState?.pageImage?.url || ""}
						/>
					)}
					<div
						style={{
							display: "inline-block",
							width: "390px",
						}}
					/>
				</Flex>
			</Flex>
		</div>
	);
}

export function ContentSegmentsEditor() {
	const segmentInDefaultValue = useFormValue("segmentIn") as string[];
	const segmentNotInDefaultValue = useFormValue("segmentNotIn") as string[];

	const { data: segmentsData } = useSegmentsQuery();
	const [countQuery, setCountQuery] = useState<{ segmentIn: string[]; segmentNotIn: string[] }>({
		segmentIn: segmentInDefaultValue || [],
		segmentNotIn: segmentNotInDefaultValue || [],
	});

	const { data, loading } = useCountUsersWithSegmentsQuery({
		variables: countQuery,
		skip: !countQuery.segmentIn.length && !countQuery.segmentNotIn.length,
		fetchPolicy: "cache-and-network",
	});

	const total = (data?.countUsersWithSegments?.total as number) ?? 0;
	const inSegments = (data?.countUsersWithSegments?.inSegments as number) ?? 0;

	const options =
		segmentsData?.segments?.map((s) => {
			return {
				...s,
				group: featureFlagLabelToGroup(s.label ?? ""),
			};
		}) ?? [];

	return (
		<Flex gap="16px" direction={Direction.column}>
			<Flex gap="16px">
				<MultiSelectField
					key={`segmentIn-${segmentsData?.segments?.length ?? 0}`}
					style={{ height: "300px" }}
					name="segmentIn"
					label="Synlig for brukere med segment"
					onChange={(value) => {
						setCountQuery({
							...countQuery,
							segmentIn: value,
						});
					}}
					options={options}
				/>
				<MultiSelectField
					key={`segmentNotIn-${segmentsData?.segments?.length ?? 0}`}
					style={{ height: "300px" }}
					name="segmentNotIn"
					label="Synlig for brukere uten segment"
					onChange={(value) => {
						setCountQuery({
							...countQuery,
							segmentNotIn: value,
						});
					}}
					options={options}
				/>
			</Flex>
			{!loading && (
				<div>
					Synlig for {inSegments} av {total} brukere
				</div>
			)}

			{loading && <div>Beregner segmentering...</div>}
		</Flex>
	);
}

interface ContentSegmentsEditorProps {
	segmentIn: string[];
	segmentNotIn: string[];
	setSegmentIn: React.Dispatch<React.SetStateAction<string[]>>;
	setSegmentNotIn: React.Dispatch<React.SetStateAction<string[]>>;
}

export function NonZodContentSegmentsEditor({ segmentIn, segmentNotIn, setSegmentIn, setSegmentNotIn }: ContentSegmentsEditorProps) {
	const { data: segmentsData } = useSegmentsQuery();

	const { data, loading } = useCountUsersWithSegmentsQuery({
		variables: { segmentIn, segmentNotIn },
		skip: !segmentIn.length && !segmentNotIn.length,
		fetchPolicy: "cache-and-network",
	});

	const total = (data?.countUsersWithSegments?.total as number) ?? 0;
	const inSegments = (data?.countUsersWithSegments?.inSegments as number) ?? 0;

	const options =
		segmentsData?.segments?.map((s) => {
			return {
				...s,
				group: featureFlagLabelToGroup(s.label ?? ""),
			};
		}) ?? [];

	return (
		<Flex gap="16px" direction={Direction.column}>
			<Flex gap="16px">
				<MultiSelectField
					value={segmentIn}
					key={`segmentIn-${segmentsData?.segments?.length ?? 0}`}
					style={{ height: "300px" }}
					label="Synlig for brukere med segment"
					onChange={(value) => {
						setSegmentIn(value);
					}}
					options={options}
				/>
				<MultiSelectField
					value={segmentNotIn}
					key={`segmentNotIn-${segmentsData?.segments?.length ?? 0}`}
					style={{ height: "300px" }}
					label="Synlig for brukere uten segment"
					onChange={(value) => {
						setSegmentNotIn(value);
					}}
					options={options}
				/>
			</Flex>
			{!loading && (
				<div>
					Synlig for {inSegments} av {total} brukere
				</div>
			)}

			{loading && <div>Beregner segmentering...</div>}
		</Flex>
	);
}
